/* @flow */
import React from 'react';

import createChainedFunction from 'utils/createChainedFunction';
import KeyConstants from 'constants/KeyConstants';
import Layer from 'components/Layer.jsx';
import LayerTrigger from 'components/LayerTrigger.jsx';
import List from 'components/List.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import Text from 'components/Text.jsx';

class Typeahead extends React.Component {
  props: {
    results: *[],
    query: string,
    // Function that renders the list of results
    listRenderer: Function,
    // Function that renders a result
    itemRenderer: Function,
    // Function that renders a "Select all" item
    selectAllItemRenderer: ?Function,
    // Function that renders a no more items prompt
    noMoreItemsRenderer: Function,
    // Function that renders a no results prompt
    noResultsRenderer: Function,
    // Show the list of results inline rather than in a layer
    inline: boolean,
    // Callback when an item is selected
    onSelect: Function,
    // Callback when the "Select all" item is selected
    onSelectAll: Function,
    // Hide the results list
    hideResults: boolean,
    onHighlightIndexChange: Function,
    onEnter: Function,
    className: ?string,
    children?: ?any,
  };

  static defaultProps = {
    listRenderer: (items, loading) => {
      return (
        <List>
          {items}
          {loading && <LoadingIndicator />}
        </List>
      );
    },
    itemRenderer: (item, highlightIndex, query, onSelect) => {
      return (
        <Text onClick={onSelect} weight={highlightIndex ? 'bold' : null}>
          {item}
        </Text>
      );
    },
    noMoreItemsRenderer: () => undefined,
    noResultsRenderer: () => undefined,
    hideResults: false,
    onHighlightIndexChange: (index: number) => undefined, // eslint-disable-line
    onEnter: () => undefined,
  };

  state = {
    highlightIndex: -1,
    loading: false,
  };

  componentDidMount() {
    if (!this.props.inline) {
      // Hide the layer on window resize
      window.addEventListener('resize', this.hideLayer.bind(this), false);
    }
  }

  componentWillReceiveProps(nextProps: Object) {
    if (this.props.query !== nextProps.query) {
      if (nextProps.query === '') {
        this.setState({ highlightIndex: -1 });
      } else {
        this.setState({ highlightIndex: 0 });
      }
    }
  }

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    const propsChanged = this.props.query !== nextProps.query ||
      this.props.results.length !== nextProps.results.length ||
      this.props.children !== nextProps.children;
    const stateChanged = this.state.highlightIndex !== nextState.highlightIndex;
    return propsChanged || stateChanged;
  }

  componentDidUpdate(prevProps: Object, prevState: Object) {
    if (this.refs.trigger) {
      this.refs.trigger.updateLayerPosition();
    }
    if (this.state.highlightIndex !== prevState.highlightIndex) {
      this.props.onHighlightIndexChange(this.state.highlightIndex);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.hideLayer.bind(this), false);
  }

  // typecheck: ignore statement
  handleKeyDown(event: SyntheticKeyboardEvent) {
    const { keyCode } = event;
    switch (keyCode) {
      case KeyConstants.DOWN:
      case KeyConstants.UP: {
        const { highlightIndex } = this.state;
        if (keyCode === KeyConstants.UP) {
          this.setState({
            highlightIndex: highlightIndex > 0 ? highlightIndex - 1 : this.props.results.length - 1,
          });
        } else {
          this.setState({
            highlightIndex: highlightIndex + 1 < this.props.results.length ? highlightIndex + 1 : 0,
          });
        }
        this.showLayer();
        break;
      }
      case KeyConstants.ENTER:
        if (this.state.highlightIndex >= 0) {
          this.selectItem(this.props.results[this.state.highlightIndex]);
        } else {
          this.props.onEnter();
        }
        break;
      default:
        this.showLayer();
        break;
    }
  }

  handleInputFocus() {
    setTimeout(() => this.showLayer(), 0);
  }

  showLayer() {
    if (!this.refs.trigger || this.refs.trigger.isLayerShown()) {
      return;
    }
    this.refs.trigger.showLayer();
  }

  hideLayer() {
    if (!this.refs.trigger || !this.refs.trigger.isLayerShown()) {
      return;
    }
    this.refs.trigger.hideLayer();
    this.resetHighlight();
  }

  resetHighlight() {
    if (this.state.highlightIndex >= 0) {
      this.setState({ highlightIndex: -1 });
    }
  }

  selectItem(datum: mixed) {
    this.resetHighlight();
    if (datum === '_all') {
      this.props.onSelectAll();
    } else {
      this.props.onSelect(datum);
    }
  }

  renderInput() {
    const input = React.Children.only(this.props.children);

    return React.cloneElement(input, {
      onFocus: createChainedFunction(() => this.handleInputFocus(), input.props.onFocus),
    });
  }

  renderList() {
    const { query, hideResults } = this.props;
    if (hideResults) {
      return null;
    }

    const items = this.props.results.map((datum, index) => {
      const item = this.renderListItem(datum, index === this.state.highlightIndex, query);
      return React.cloneElement(item, {
        key: index,
        onMouseEnter: createChainedFunction(
          () => {
            if (index !== this.state.highlightIndex) {
              this.setState({ highlightIndex: index });
            }
          },
          item.props.onMouseEnter
        ),
        onMouseLeave: createChainedFunction(
          () => {
            if (index === this.state.highlightIndex) {
              this.setState({ highlightIndex: -1 });
            }
          },
          item.props.onMouseLeave
        ),
      });
    });

    if (items.length === 0) {
      items.push(query ? this.props.noResultsRenderer(query) : this.props.noMoreItemsRenderer());
    }
    return this.props.listRenderer(items, this.state.loading);
  }

  renderListItem(item: mixed, highlighted: boolean, query: string) {
    switch (item) {
      case '_all':
        if (!this.props.selectAllItemRenderer) {
          return <b>error</b>;
        }
        return this.props.selectAllItemRenderer(highlighted, () => this.selectItem('_all'));
      default:
        return this.props.itemRenderer(item, highlighted, query, (i = item) => this.selectItem(i));
    }
  }

  renderLayer() {
    return (
      <Layer
        style={{ margin: '0 44px' }}
        onMouseLeave={() => this.resetHighlight()}
        onRequestHide={() => this.resetHighlight()}>
        {this.renderList()}
      </Layer>
    );
  }

  render() {
    const props = { ...this.props, onSelect: null };

    if (this.props.inline) {
      return (
        <div className={this.props.className}>
          {this.renderInput()}
          {this.renderList()}
        </div>
      );
    }

    return (
      <LayerTrigger
        ref="trigger"
        className={props.className}
        layer={this.renderLayer()}
        trigger="manual"
        placement="bottom"
        useContextWidth>
        {this.renderInput()}
      </LayerTrigger>
    );
  }
}

export default Typeahead;



// WEBPACK FOOTER //
// ./src/components/Typeahead.jsx